import React, { lazy, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';
import DataThresholdingIcon from '@mui/icons-material/DataThresholding';

import ExportDataBySensorsButton from './button';
import ExportDataBySensorsWindow from './window';
import getDefaultDates from './utils/getDefaultDates';

const ExportDataBySensorsForm = lazy(() => import('./ExportDataBySensorsForm/ExportDataBySensorsForm'));

function ExportDataBySensors({ vehicleId, geoZones, dateFrom, dateTo }) {
  const isGeoZonesExport = !!geoZones?.length;
  const isVehicleExport = !!vehicleId;
  const canOpenDialog = isVehicleExport || isGeoZonesExport;

  const [isOpen, setIsOpen] = useState(false);

  const defaultDates = useMemo(() => getDefaultDates(), []);
  const { startOfMonth, tomorrowMidnight } = defaultDates;

  const [formState, setFormState] = useState({
    dateFrom: dateFrom || startOfMonth,
    dateTo: dateTo || tomorrowMidnight,
    sensors: [],
    fileFormat: 'SHAPE',
    graphicPrimitive: 'polygon',
    approxType: 'NONE',
    isCombine: true,
  });

  useEffect(() => {
    setFormState((prev) => ({
      ...prev,
      dateFrom: dateFrom || startOfMonth,
      dateTo: dateTo || tomorrowMidnight,
    }));
  }, [dateFrom, dateTo]);

  const handleClose = useCallback(() => setIsOpen(false), []);
  const handleOpen = useCallback(() => setIsOpen(true), []);

  const form = useMemo(
    () =>
      canOpenDialog ? (
        <ExportDataBySensorsForm
          formState={formState}
          setFormState={setFormState}
          onClose={handleClose}
          geoZones={geoZones}
          vehicleId={vehicleId}
        />
      ) : null,
    [handleClose, geoZones, formState, vehicleId, canOpenDialog],
  );

  useEffect(() => {
    if (!isGeoZonesExport && isOpen && !isVehicleExport) {
      toast.error('Геозоны были утеряны, начните процесс заново.');

      setIsOpen(false);
    }
  }, [isOpen, geoZones, isGeoZonesExport, isVehicleExport]);

  return (
    <>
      <Suspense>
        <ExportDataBySensorsWindow isOpen={isOpen} onClose={handleClose}>
          {form}
        </ExportDataBySensorsWindow>
      </Suspense>

      <ExportDataBySensorsButton disabled={!canOpenDialog} onClick={handleOpen}>
        <DataThresholdingIcon />
      </ExportDataBySensorsButton>
    </>
  );
}

ExportDataBySensors.propTypes = {
  vehicleId: PropTypes.number,
  geoZones: PropTypes.arrayOf(PropTypes.shape()),
  dateFrom: PropTypes.instanceOf(Date),
  dateTo: PropTypes.instanceOf(Date),
};

ExportDataBySensors.defaultProps = {
  vehicleId: null,
  geoZones: [],
  dateFrom: null,
  dateTo: null,
};

export default React.memo(ExportDataBySensors);
